import CartographerReporter, {
  THIRTY_SECONDS,
  RouteInfo,
  NavigationAction,
} from './CartographerReporter';
import { isReload } from '../navigation';
import { captureException } from '../ravenUtils';
import { ReporterAction } from '../types';
import { onVisibilityHidden } from '../visibility';
import {
  resetFirstInputPolyfill,
  firstInputPolyfill,
  FirstInputPolyfillCallback,
} from '../BFCacheRestore';
import { ReporterOptions } from './BaseReporter';
import performanceNow from '../../vendor/performanceNow';
import {
  PerformanceEventTiming,
  LargestContentfulPaint,
  LayoutShift,
} from '../performanceApi';
import PerformanceEventBus from '../PerformanceEventBus';
import PageStateTracker from '../PageStateTracker';

interface onBFCacheRestoreCallback {
  (event: PageTransitionEvent): void;
}

const onBFCacheRestore = (callback: onBFCacheRestoreCallback) => {
  window.addEventListener(
    'pageshow',
    (event) => {
      if (event.persisted) {
        callback(event);
      }
    },
    true
  );
};

interface PerformanceInteraction {
  latency: number;
  entries: PerformanceEntry[];
  interactionNames: string;
}

const DEFERRED_PAINT_THRESHOLD = 10000;

const GENERIC_TAGS = new Set([
  'div',
  'span',
  'section',
  'main',
  'header',
  'footer',
  'nav',
  'article',
  'aside',
  'p',
  'li',
  'ul',
  'ol',
]);
const MAX_CLASSES = 3;
const MAX_ANCESTOR_DEPTH = 5;
const MAX_ARIA_LABEL_LENGTH = 40;

function getAttr(el: Element, attr: string): string {
  return el.getAttribute(attr) || '';
}

function buildSelfSelector(el: Element): string {
  const tag = el.tagName?.toLowerCase?.();
  if (!tag) {
    return '';
  }
  let selector = tag;

  if (el.id) {
    selector += `#${el.id}`;
  }

  const classNames = getAttr(el, 'class');
  if (classNames.trim()) {
    const classes = classNames.trim().split(/\s+/);
    selector += `.${classes.slice(0, MAX_CLASSES).join('.')}`;
    if (classes.length > MAX_CLASSES) {
      selector += '...';
    }
  }

  const testId = getAttr(el, 'data-test-id');
  if (testId) {
    selector += `[data-test-id="${testId}"]`;
  }

  const role = getAttr(el, 'role');
  if (role && role !== 'presentation' && role !== 'none') {
    selector += `[role="${role}"]`;
  }

  const ariaLabel = getAttr(el, 'aria-label');
  if (ariaLabel) {
    const label =
      ariaLabel.length > MAX_ARIA_LABEL_LENGTH
        ? `${ariaLabel.slice(0, MAX_ARIA_LABEL_LENGTH)}...`
        : ariaLabel;
    selector += `[aria-label="${label}"]`;
  }

  if (['input', 'textarea', 'select'].includes(tag)) {
    const name = getAttr(el, 'name');
    if (name) {
      selector += `[name="${name}"]`;
    }
  }

  if (tag === 'img') {
    const alt = getAttr(el, 'alt');
    if (alt) {
      const truncated =
        alt.length > MAX_ARIA_LABEL_LENGTH
          ? `${alt.slice(0, MAX_ARIA_LABEL_LENGTH)}...`
          : alt;
      selector += `[alt="${truncated}"]`;
    }
  }

  return selector;
}

function isDescriptive(selector: string, tag: string): boolean {
  if (!selector) {
    return false;
  }
  return selector !== tag || !GENERIC_TAGS.has(tag);
}

function buildAncestorPrefix(el: Element): string {
  const tag = el.tagName?.toLowerCase?.();
  if (!tag) {
    return '';
  }
  let prefix = tag;
  if (el.id) {
    prefix += `#${el.id}`;
  }
  const testId = getAttr(el, 'data-test-id');
  if (testId) {
    prefix += `[data-test-id="${testId}"]`;
  }
  const idMarker = getAttr(el, 'data-id-marker');
  if (idMarker) {
    prefix += `[data-id-marker="${idMarker}"]`;
  }
  if (!testId && !idMarker) {
    const classNames = getAttr(el, 'class');
    if (classNames.trim()) {
      prefix += `.${classNames.trim().split(/\s+/)[0]}`;
    }
  }
  return prefix;
}

function getElementSelector(node: Node | EventTarget | null): string {
  if (!node || !('tagName' in node)) {
    return '';
  }
  const el = node as Element;

  const selfSelector = buildSelfSelector(el);

  let ancestorPart = '';
  let current: Element | null = el.parentElement;
  let depth = 0;
  while (current && depth < MAX_ANCESTOR_DEPTH) {
    depth++;
    const ancestorPrefix = buildAncestorPrefix(current);
    const ancestorTag = current.tagName?.toLowerCase?.() ?? '';
    if (isDescriptive(ancestorPrefix, ancestorTag)) {
      const separator = depth === 1 ? ' > ' : ' .. ';
      ancestorPart = ancestorPrefix + separator;
      break;
    }
    current = current.parentElement;
  }

  return ancestorPart + selfSelector;
}

export default class VitalsReporter extends CartographerReporter {
  private static readonly WINDOW_KEY = '__rhumb_vitals_reporter__';

  /**
   * Get the singleton instance of VitalsReporter.
   * Uses window-scoped storage so the instance is shared across all JS bundles
   * on the same page, preventing duplicate LCP reports.
   */
  public static getInstance(options?: ReporterOptions): VitalsReporter {
    const w = window as any;
    if (!w[VitalsReporter.WINDOW_KEY]) {
      w[VitalsReporter.WINDOW_KEY] = new VitalsReporter(options);
    }
    return w[VitalsReporter.WINDOW_KEY];
  }

  public static resetInstance(): void {
    (window as any)[VitalsReporter.WINDOW_KEY] = undefined;
  }

  lastRouteInfo: RouteInfo | undefined;
  resolved: { [id: number]: boolean };
  stopped: boolean;
  flushQueueTimeout: number | undefined;
  firstHiddenTime: number;
  interactionMap: Map<number, PerformanceInteraction>;
  latestLCP: {
    route: RouteInfo | undefined;
    data: Pick<
      LargestContentfulPaint,
      'entryType' | 'size' | 'startTime' | 'loadTime' | 'renderTime'
    > & {
      element: string;
      elapsedMs: number;
      id: string;
      url: string;
    };
  } | null;
  lastStatus: NavigationAction['status'] | undefined;
  lastFailureType: NavigationAction['failureType'] | undefined;

  getPageState() {
    const pageStateTracker = PageStateTracker.getInstance();
    const pageStateSummary = pageStateTracker.getStateSummary();
    const currentSnapshot = pageStateTracker.getSnapshot();

    return {
      currentOnline: currentSnapshot.online,
      currentHidden: currentSnapshot.hidden,
      wasEverHidden: pageStateSummary.wasEverHidden,
      wasEverOffline: pageStateSummary.wasEverOffline,
    };
  }

  private constructor(options?: ReporterOptions) {
    super(options);
    this.resolved = {};
    this.stopped = false;
    this.firstHiddenTime = document.visibilityState === 'hidden' ? 0 : Infinity;
    this.interactionMap = new Map();
    this.latestLCP = null;
    this.lastStatus = undefined;
    this.lastFailureType = undefined;

    window.addEventListener(
      'visibilitychange',
      (event) => {
        this.firstHiddenTime = Math.min(this.firstHiddenTime, event.timeStamp);
      },
      { once: true }
    );

    const debounce = () => {
      clearTimeout(this.flushQueueTimeout);
      this.flushQueueTimeout = setTimeout(() => {
        this.flushPerformanceQueue();
        // clear the map once the performance action buffer is cleared, to avoid memory leak
        this.interactionMap.clear();
      }, THIRTY_SECONDS);
    };

    const processFIDEntry = (perfEntry: PerformanceEventTiming | unknown) => {
      try {
        // some browsers no longer support FID and send an error instead of a PerformanceEventTiming object
        if (
          perfEntry &&
          typeof perfEntry === 'object' &&
          'toJSON' in perfEntry &&
          typeof perfEntry.toJSON === 'function'
        ) {
          const {
            processingStart: processingStart,
            startTime: startTime,
            ...perfData
          } = (perfEntry as PerformanceEventTiming).toJSON();
          // ignore entries if the page was not in the foreground when the first input occured
          if (startTime < this.firstHiddenTime) {
            const inputDelay = processingStart - startTime;
            this.pushPerformanceAction(this.lastRouteInfo, 'first-input', {
              ...perfData,
              processingStart,
              startTime,
              inputDelay,
              pageState: this.getPageState(),
            });
            debounce();
          }
        }
      } catch (e) {
        captureException(e instanceof Error ? e : new Error(String(e)));
      }
    };

    const processUserInteractionEntry = (perfEntry: PerformanceEventTiming) => {
      try {
        const { interactionId, target, name } = perfEntry;
        if (interactionId > 0) {
          // When a tab goes to the background between an interaction and the
          // next paint, the browser defers the paint until the tab is visible
          // again — inflating `duration` to include the entire hidden period
          // (we've seen values in the billions of ms). Filter these out by
          // comparing duration to the actual processing time.
          const processingTime = perfEntry.processingEnd - perfEntry.startTime;
          if (perfEntry.duration - processingTime > DEFERRED_PAINT_THRESHOLD) {
            return;
          }

          let interaction = this.interactionMap.get(interactionId);
          if (!interaction) {
            interaction = { latency: 0, entries: [], interactionNames: '' };
            this.interactionMap.set(interactionId, interaction);
          }
          interaction.entries.push(perfEntry);
          interaction.latency = Math.max(
            perfEntry.duration,
            interaction.latency
          );
          const testId =
            target && (target as Element)?.hasAttribute('data-test-id')
              ? (target as Element)?.getAttribute('data-test-id')
              : '';

          const tagName = getElementSelector(target);

          if (name && name.length > 0) {
            interaction.interactionNames = interaction.interactionNames.concat(
              interaction.interactionNames.length > 0 ? ' ' : '',
              name
            );
          }
          this.evictPerformanceAction(interactionId);
          this.pushPerformanceAction(this.lastRouteInfo, 'event', {
            interactionId,
            interactionNames: interaction.interactionNames,
            latency: interaction.latency,
            elapsedMs: performanceNow(),
            testId,
            tagName,
            pageState: this.getPageState(),
          });
          debounce();
        }
      } catch (e) {
        captureException(e instanceof Error ? e : new Error(String(e)));
      }
    };

    const entryHandler = (entry: PerformanceEventTiming) => {
      processFIDEntry(entry);
    };

    /*
      When the page is restored from the back/forward cache, re-add event listeners for FID
      and manually push FID data to the performance actions queue since the performance observer
      won't be able to catch it.
    */
    onBFCacheRestore(() => {
      resetFirstInputPolyfill();
      firstInputPolyfill(entryHandler as FirstInputPolyfillCallback);
    });

    // Observe performance entries
    PerformanceEventBus.getInstance().subscribe(
      'long-animation-frame',
      (perfEntry) => {
        try {
          // For each script in the long animation frame, emit a longtask event
          // Only log scripts with duration >= 50ms
          perfEntry.scripts.forEach((script) => {
            if (script.duration >= 50) {
              const scriptData = script.toJSON();

              this.pushPerformanceAction(this.lastRouteInfo, 'longtask', {
                name: script.windowAttribution,
                ...scriptData,
                entryType: 'longtask',
                pageState: this.getPageState(),
              });
            }
          });
          debounce();
        } catch (e) {
          captureException(e instanceof Error ? e : new Error(String(e)));
        }
      }
    );

    PerformanceEventBus.getInstance().subscribe('first-input', (perfEntry) => {
      processFIDEntry(perfEntry);
    });

    PerformanceEventBus.getInstance().subscribe('event', (perfEntry) => {
      processUserInteractionEntry(perfEntry);
    });

    PerformanceEventBus.getInstance().subscribe('paint', (perfEntry) => {
      try {
        if (
          perfEntry.name === 'first-contentful-paint' &&
          perfEntry.startTime < this.firstHiddenTime
        ) {
          const paintData = {
            name: perfEntry.name,
            startTime: perfEntry.startTime,
            entryType: perfEntry.entryType,
            elapsedMs: performanceNow(),
            pageState: this.getPageState(),
          };

          this.pushPerformanceAction(this.lastRouteInfo, 'paint', paintData);
          debounce();
        }
      } catch (e) {
        captureException(e instanceof Error ? e : new Error(String(e)));
      }
    });
    PerformanceEventBus.getInstance().subscribe(
      'largest-contentful-paint',
      (perfEntry) => {
        try {
          const element = perfEntry.element;
          const { entryType, size, startTime, renderTime, loadTime, id, url } =
            perfEntry.toJSON();
          if (startTime >= this.firstHiddenTime) {
            return;
          }
          const paintData = {
            entryType,
            size,
            startTime,
            renderTime,
            element: getElementSelector(element ?? null),
            loadTime,
            elapsedMs: performanceNow(),
            id,
            url,
            pageState: this.getPageState(),
          };
          // Store the latest LCP instead of pushing immediately
          // It will be pushed when the page becomes hidden or user interacts
          this.latestLCP = {
            route: this.lastRouteInfo,
            data: paintData,
          };
        } catch (e) {
          captureException(e instanceof Error ? e : new Error(String(e)));
        }
      }
    );

    // Handle first user interaction to finalize LCP
    const finalizeOnInteraction = () => {
      try {
        if (this.latestLCP) {
          this.pushPerformanceAction(
            this.latestLCP.route,
            'largest-contentful-paint',
            {
              ...this.latestLCP.data,
              status: this.lastStatus,
              failureType: this.lastFailureType,
              isReload: isReload(),
            }
          );
          this.latestLCP = null;
          debounce();
        }
      } catch (e) {
        captureException(e instanceof Error ? e : new Error(String(e)));
      }
    };

    PerformanceEventBus.getInstance().subscribe(
      'layout-shift',
      (perfEntry: LayoutShift) => {
        try {
          // Filter out user-initiated shifts and insignificant shifts
          if (perfEntry.hadRecentInput || perfEntry.value < 0.001) {
            return;
          }

          const { entryType, value, startTime, hadRecentInput, lastInputTime } =
            perfEntry.toJSON();
          const sources = (perfEntry.sources ?? []).map((source) => ({
            element: getElementSelector(source.node),
            previousRect: source.previousRect,
            currentRect: source.currentRect,
          }));
          const layoutShiftData = {
            entryType,
            value,
            startTime,
            sources,
            hadRecentInput,
            lastInputTime,
            elapsedMs: performanceNow(),
            pageState: this.getPageState(),
          };
          this.pushPerformanceAction(
            this.lastRouteInfo,
            'layout-shift',
            layoutShiftData
          );
          debounce();
        } catch (e) {
          captureException(e instanceof Error ? e : new Error(String(e)));
        }
      }
    );
    // Store cleanup functions for interaction listeners
    const interactionCleanupFunctions: (() => void)[] = [];

    // Listen for first interaction events that finalize LCP
    const eventTypes: Array<
      'keydown' | 'mousedown' | 'pointerdown' | 'touchstart'
    > = ['keydown', 'mousedown', 'pointerdown', 'touchstart'];

    eventTypes.forEach((eventType) => {
      const handler = () => {
        finalizeOnInteraction();
        // Clean up remaining listeners after first interaction
        interactionCleanupFunctions.forEach((cleanup) => cleanup());
      };

      window.addEventListener(eventType, handler, {
        capture: true,
        once: true,
      });

      interactionCleanupFunctions.push(() =>
        window.removeEventListener(eventType, handler, { capture: true })
      );
    });

    // Handle visibility change to flush remaining entries
    onVisibilityHidden(() => {
      try {
        // Push the final LCP before flushing
        if (this.latestLCP) {
          this.pushPerformanceAction(
            this.latestLCP.route,
            'largest-contentful-paint',
            {
              ...this.latestLCP.data,
              status: this.lastStatus,
              failureType: this.lastFailureType,
              isReload: isReload(),
            }
          );
          this.latestLCP = null;
        }
        // Clean up interaction listeners since LCP is now finalized
        interactionCleanupFunctions.forEach((cleanup) => cleanup());
        // Flush any remaining performance actions
        this.flushPerformanceQueue();
      } catch (e) {
        captureException(e instanceof Error ? e : new Error(String(e)));
      }
    });
  }

  destroy() {
    // VitalsReporter is a singleton shared across all RhumbProvider instances;
    // its listeners must persist for the page lifetime.
  }

  evictPerformanceAction(interactionId: number) {
    this.performanceActions = this.performanceActions.filter(
      (action) => action.data.interactionId !== interactionId
    );
  }

  __setFirstHiddenTime(time: number) {
    this.firstHiddenTime = time;
  }

  report(action: ReporterAction) {
    if (
      action.type === 'COMPONENT_RENDERED' ||
      this.resolved[action.payload.entry.id] ||
      this.stopped
    ) {
      return;
    }

    switch (action.type) {
      case 'ROUTE_TIMEOUT_EXPIRED': {
        const {
          routeSpec: { route },
        } = action.payload;
        this.lastRouteInfo = { route };
        this.lastStatus = 'failure';
        this.lastFailureType = 'watchdogExpired';
        break;
      }
      case 'GLOBAL_ERROR':
      case 'ROUTE_FAILED': {
        const {
          entry: { pathname },
          routeSpec,
        } = action.payload;
        const { route } = routeSpec;
        this.lastRouteInfo = {
          pathname,
          route,
        };
        this.lastStatus = 'failure';
        this.lastFailureType =
          action.type === 'ROUTE_FAILED' ? 'errorSelector' : undefined;
        break;
      }
      case 'ROUTE_PARTIAL_SUCCESS': {
        const {
          entry: { pathname },
          extra: { scenario },
          routeSpec,
        } = action.payload;
        const { route } = routeSpec;
        this.lastRouteInfo = {
          pathname,
          route,
          scenario,
        };
        this.lastStatus = 'partial_success';
        this.lastFailureType = undefined;
        break;
      }
      case 'ROUTE_SUCCEEDED': {
        const {
          entry: { pathname },
          extra: { scenario },
          routeSpec,
        } = action.payload;
        const { route } = routeSpec;
        this.lastRouteInfo = {
          pathname,
          route,
          scenario,
        };
        this.lastStatus = 'success';
        this.lastFailureType = undefined;
        break;
      }
      default:
    }

    switch (action.type) {
      case 'ROUTE_UNEXPECTED': {
        this.stopped = true;
        break;
      }
      case 'GLOBAL_ERROR':
      case 'ROUTE_PARTIAL_SUCCESS':
      case 'ROUTE_SUCCEEDED':
      case 'ROUTE_TIMEOUT_EXPIRED':
      case 'ROUTE_FAILED': {
        this.resolved[action.payload.entry.id] = true;
        break;
      }
      default:
    }
  }
}
